import { AuthContext } from 'context';
import formatPartCode from "helper/formatPartCode";
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InventoryService from 'services/inventory-items-service';
import partCodesService from "services/part-code-service";

// Material Dashboard React components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Slide from "@mui/material/Slide";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";

// Inventory Management Components
import ConfirmDialog from "e4acomponents/ConfirmDialog";
import ItemFields from '../components/item-fields';
import PartCodeDetails from "../components/partcode-details";
import SelectPartCode from "../components/select-partcode";

export default function CreateInventoryItem() {
    const [inventoryItem, setInventoryItem] = useState({
        partCode: "",
        quantityIn: 0,
        locationId: "",
        location: "",
        rack: "",
        shelf: "",
        drawer: "",
        pricePerUnit: 0,
        identifier: "",
        currency: "CAD",
    });
    const [pageTitle, setPageTitle] = useState("");
    const [selectedPartCode, setSelectedPartCode] = useState(null);
    const [showPartCodeDetails, setShowPartCodeDetails] = useState(false);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        title: "",
        id: "",
        prompt: "",
        onClose: null
    });

    const navigate = useNavigate();
    const { partCodeId } = useParams();
    const authContext = useContext(AuthContext);

    useEffect(() => {
        if (partCodeId) {
            const fetchPartCode = async () => {
                try {
                    const response = await partCodesService.getPartById(partCodeId);
                    const identifier = formatPartCode(response.data.attributes);

                    // Assuming response.data contains the part code object
                    setSelectedPartCode(response.data.attributes);
                    setPageTitle(identifier.replace(/-/g, '–'));
                    setInventoryItem(prev => ({
                        ...prev,
                        identifier: identifier,
                    }))
                } catch (err) {
                    console.error("Error fetching part code:", err);
                }
            };
            fetchPartCode();
        }
    }, [partCodeId]);

    // Shows the cards after the data is fetched from the API
    useEffect(() => {
        if (selectedPartCode && !showPartCodeDetails) {
            setShowPartCodeDetails(true);
        } else {

            setShowPartCodeDetails(false);
        }
        changeHandler("partCode", selectedPartCode ? selectedPartCode._id : "");
    }, [selectedPartCode]);

    // Refresh the partcodeDetails card if another partcode is selected
    useEffect(() => {
        if (selectedPartCode && !showPartCodeDetails) {
            setTimeout(() => {
                setShowPartCodeDetails(true);
            }, 1000);
        }
    }, [showPartCodeDetails])

    // Change Handler for the inventory item
    const changeHandler = (target, value) => {
        setInventoryItem({
            ...inventoryItem,
            [target]: value
        });
    }

    // Batch Change Handler for multiple fields at once
    const batchChangeHandler = (updates) => {
        setInventoryItem({
            ...inventoryItem,
            ...updates
        });
    }

    // Confirm dialog close handler
    const onClose = async (response, title, id) => {
        // Handle dialog close response if needed
        if (response) {
            await submitToApi();
        }
        setOpenDialog(false);
    }

    // Open confirm dialog handler
    const handleOpenDialog = (title, prompt, id) => {
        setConfirmDialogProps({
            title: title,
            prompt: prompt,
            onClose: onClose,
            id: id
        });
        setOpenDialog(true);
    }

    // Form Validation
    const validateForm = () => {
        const requiredFields = [
            'partCode',
            'locationId'
        ];

        const newErrors = {};

        for (const field of requiredFields) {
            if (!inventoryItem[field]) {
                newErrors[field] = `This ${field} is required.`;
            }
        }
        return newErrors;
    }

    // Submit Handler for adding the inventory item to the database
    const submitHandler = async () => {
        // Validate form before submitting
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setError(formErrors);
            return;
        }

        if (!inventoryItem.locationId || !inventoryItem.shelf || !inventoryItem.drawer) {
            await submitToApi();
            return;
        }

        const response = await InventoryService.checkDrawerOccupation(
            inventoryItem.locationId,
            inventoryItem.shelf,
            inventoryItem.drawer,
            null
        )

        if (response?.data?.attributes?.currentNumItems > 0) {
            handleOpenDialog(
                "Drawer Occupied",
                `The selected drawer (${inventoryItem.location} / ${inventoryItem.rack} / ${inventoryItem.drawer}) is already occupied by ${response.data.attributes.currentNumItems} item(s). Do you wish to continue ?`,
                null
            )
        } else {
            await submitToApi();
        }
    }


    const submitToApi = async () => {
        try {
            const payload = {
                data: {
                    type: "inventoryItem",
                    attributes: inventoryItem,
                    logs: {
                        user: await authContext.getCurrentUser()
                    }
                }
            }
            const response = await InventoryService.createInventoryItem(payload);

            navigate("/inventory-management", {
                state: { value: false, text: "The Item was succesfully created" },
            });
        } catch (err) {
            console.log(err);
        }
    }
    return (
        <DashboardLayout>
            {pageTitle ?
                <DashboardNavbar breadcrumbTitle={pageTitle} /> :
                <DashboardNavbar />
            }

            <ConfirmDialog
                open={openDialog}
                props={confirmDialogProps}
            />

            <MDBox mt={5} mb={9}>
                <Grid container justifyContent="center" spacing={2}>

                    {/* Title */}
                    <Grid item xs={12} lg={12} >
                        <MDBox
                            mt={6}
                            mb={4}
                            textAlign="center"
                        >
                            <MDBox mb={1}>
                                <MDTypography variant="h3" fontWeight="bold">
                                    Create A New Inventory Item
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                Please fill all the fields to create a new inventory item.
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={1000}>
                        <Grid item xs={12} lg={6}>

                            {/* Part Code Selection */}
                            {!partCodeId && (
                                <SelectPartCode
                                    selectedPartCode={selectedPartCode}
                                    setSelectedPartCode={setSelectedPartCode}
                                    changeHandler={changeHandler}
                                    error={error}
                                    editMode={false}
                                />)}

                            {/* Inventory Item Details */}
                            <ItemFields
                                inventoryItem={inventoryItem}
                                changeHandler={changeHandler}
                                batchChangeHandler={batchChangeHandler}
                                submitHandler={submitHandler}
                                error={error}
                                editMode={false}
                            />

                        </Grid>
                    </Slide>


                    <Slide direction="up" in={showPartCodeDetails} mountOnEnter unmountOnExit timeout={1000}>
                        <Grid item xs={12} lg={6}>

                            <MDBox mt={partCodeId ? 2 : 0}>
                                {/* Part Code Details */}
                                <PartCodeDetails id={selectedPartCode ? selectedPartCode._id : ""} />
                            </MDBox>

                            {/* Location Format Guide */}
                            <MDBox mt={2}>
                                <Card>
                                    <MDBox p={3}>
                                        <MDTypography variant="h6" fontWeight="medium" mb={2}>
                                            Location Format Guide
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            <strong>Lx </strong>represents the location identifier<br /><br />
                                            <strong>R</strong> represents the rack number. This identifier is variable and can change based on the storage plan.<br /><br />
                                            <strong>S</strong> denotes the shelf number, which is organized sequentially from top to bottom, starting with 1 at the top and increasing to 4.<br /><br />
                                            Therefore, <strong>L1 / Rx / Sy</strong> serves as the E4A identifier, specifying the exact location of items stored on each shelf, with Rx indicating the particular rack and Sy specifying the shelf position from top to bottom.
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </MDBox>

                        </Grid>
                    </Slide>

                </Grid>
            </MDBox>

            <MDBox>
                <Footer />
            </MDBox>

        </DashboardLayout>
    )
}